import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getAffiliateCode } from "@/hooks/useAffiliateTracking";

function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[$()*+./?[\\\]^{|}]/g, "\\$&")}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

async function getAccessToken(): Promise<string> {
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) return data.session.access_token;
  } catch {
    // Retry once — handles "Lock was released" race condition
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) return data.session.access_token;
  }
  throw new Error("Not authenticated");
}

export interface CustomerSubscription {
  id: string;
  stripe_subscription_id: string;
  subscription_type: "single_tournament" | "season_pass";
  tournament_id: string | null;
  status: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
}

export function useSubscription() {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState<CustomerSubscription[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSubscriptions = useCallback(async () => {
    if (!user) {
      setSubscriptions([]);
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from("customer_subscriptions")
      .select("id, stripe_subscription_id, subscription_type, tournament_id, status, current_period_end, cancel_at_period_end")
      .eq("user_id", user.id)
      .in("status", ["active", "past_due"]);

    setSubscriptions((data as CustomerSubscription[]) || []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const hasSeasonPass = subscriptions.some(
    (s) => s.subscription_type === "season_pass" && (s.status === "active" || s.status === "trialing")
  );

  const hasAccess = useCallback(
    (tournamentId: string): boolean => {
      if (hasSeasonPass) return true;
      return subscriptions.some(
        (s) =>
          s.subscription_type === "single_tournament" &&
          s.tournament_id === tournamentId &&
          (s.status === "active" || s.status === "trialing")
      );
    },
    [subscriptions, hasSeasonPass]
  );

  const activeCount = subscriptions.filter(
    (s) => s.subscription_type === "single_tournament" && (s.status === "active" || s.status === "trialing")
  ).length;

  const createCheckout = useCallback(
    async (
      priceType: "single_tournament" | "season_pass" | "season_pass_upgrade",
      tournamentId?: string,
      returnTo?: string,
    ) => {
      const token = await getAccessToken();

      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const resp = await fetch(`${baseUrl}/functions/v1/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          priceType,
          tournamentId,
          affiliateCode: getAffiliateCode(),
          returnTo,
          // Forward Meta-pixel cookies + current URL so the stripe-webhook
          // can fire a server-side `Subscribe` event (Conversions API)
          // that dedupes with the browser pixel via event_id.
          fbp: getCookie("_fbp"),
          fbc: getCookie("_fbc"),
          eventSourceUrl: typeof window !== "undefined" ? window.location.href : undefined,
        }),
      });

      // Parse the body carefully — if the edge function crashes before
      // returning JSON we want to surface the raw text rather than a
      // useless "Checkout failed".
      const text = await resp.text();
      let data: any = {};
      try { data = text ? JSON.parse(text) : {}; } catch { /* non-JSON */ }
      if (!resp.ok) {
        const msg = data?.error || data?.message || text || `Checkout failed (HTTP ${resp.status})`;
        console.error("[checkout] edge function error:", resp.status, text);
        throw new Error(msg);
      }
      return data.url as string;
    },
    []
  );

  const manageSubscription = useCallback(
    async (action: "cancel" | "reactivate" | "portal", subscriptionId?: string) => {
      const token = await getAccessToken();

      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const resp = await fetch(`${baseUrl}/functions/v1/manage-subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action, subscriptionId }),
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Action failed");
      return data;
    },
    []
  );

  return {
    subscriptions,
    loading,
    hasAccess,
    hasSeasonPass,
    activeCount,
    createCheckout,
    manageSubscription,
    refetch: fetchSubscriptions,
  };
}
